import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useApi } from "@/core/api";
import { editorKey, editorPath } from "@/payroll/editor/query";
import type {
  ComparisonSheet,
  ComparisonState,
  ComparisonStatement,
  ComparisonStatements,
} from "@/payroll/editor/types";
import { enrichComparison, enrichComparisonSheet } from "./enrich-comparison";

// A live comparison runs both versions of the payroll over a real period, so it
// takes minutes. Poll while it is going, and leave it alone when it is not.
const RUNNING_POLL = 5000;
// And while a request is running, because the assistant starts a comparison of
// its own when it saves. Polling only on `running` could never discover one:
// the flag is false until a poll reads it, and no poll was scheduled while it
// was false, so a comparison begun with the tab open stayed invisible until
// something else happened to refetch.

export const useComparison = (
  businessId: string,
  workspaceId: string,
  periodStart: string | undefined,
  enabled: boolean,
  /** A request is running, so a comparison may start without us asking. */
  watch = false,
) => {
  const api = useApi();
  const client = useQueryClient();
  const path = `${editorPath(businessId)}/workspaces/${workspaceId}/comparison`;
  const key = [
    ...editorKey(businessId),
    workspaceId,
    "comparison",
    periodStart,
  ];

  const state = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      api
        .get<ComparisonState>(path, {
          signal,
          params: periodStart ? { date_in_period: periodStart } : undefined,
        })
        .then((response) => response.data),
    select: enrichComparison,
    enabled,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.running || watch ? RUNNING_POLL : false,
  });

  // Comparison work finishes separately from the chat turn. Refresh the cells
  // and PayPacks when its state changes; the summary alone is not their data.
  const running = state.data?.running;
  const ready = state.data?.ready;
  useEffect(() => {
    if (!ready || running) return;
    const comparisonKey = [
      ...editorKey(businessId),
      workspaceId,
      "comparison",
      periodStart,
    ];
    void client.invalidateQueries({
      queryKey: comparisonKey,
      predicate: (query) => query.queryKey.length > comparisonKey.length,
    });
  }, [businessId, workspaceId, periodStart, running, ready, client]);

  const run = useMutation({
    mutationFn: () =>
      api
        .post<ComparisonState>(
          path,
          periodStart ? { date_in_period: periodStart } : {},
        )
        .then((response) => response.data),
    onSuccess: (data) => client.setQueryData(key, data),
  });

  return { state, run, path, key };
};

export const useComparisonSheet = (
  path: string,
  key: readonly unknown[],
  periodStart: string | undefined,
  index: number | null,
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...key, "sheet", index],
    queryFn: ({ signal }) =>
      api
        .get<ComparisonSheet>(`${path}/sheets/${index}`, {
          signal,
          params: periodStart ? { date_in_period: periodStart } : undefined,
        })
        .then((response) => response.data),
    select: enrichComparisonSheet,
    enabled: index !== null,
    retry: false,
  });
};

export const useComparisonStatements = (
  path: string,
  key: readonly unknown[],
  periodStart: string | undefined,
  enabled: boolean,
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...key, "statements"],
    queryFn: ({ signal }) =>
      api
        .get<ComparisonStatements>(`${path}/statements`, {
          signal,
          params: periodStart ? { date_in_period: periodStart } : undefined,
        })
        .then((response) => response.data),
    enabled,
    retry: false,
  });
};

export const useComparisonStatement = (
  path: string,
  key: readonly unknown[],
  periodStart: string | undefined,
  technicianId: string | null,
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...key, "statement", technicianId],
    queryFn: ({ signal }) =>
      api
        .get<ComparisonStatement>(`${path}/statements/${technicianId}`, {
          signal,
          params: periodStart ? { date_in_period: periodStart } : undefined,
        })
        .then((response) => response.data),
    enabled: !!technicianId,
    retry: false,
  });
};
